import * as React from 'react';
import {reaction, IReactionDisposer} from 'mobx';
import {observer} from 'mobx-react';

import {v4 as uuid} from 'uuid';

import _isEqual from 'lodash/isEqual';
import _isFunction from 'lodash/isFunction';

import {Animation, Utility} from '@kouch.tv/brandis-uikit';

import {CN} from '@kouch.tv/cn';

import {PlatformModel, RuntimeModel} from 'src/application';
import {ICommonPropTypes} from 'src/types';

import style from './style.less';


export interface IVideoOverlayControlsPropTypes extends ICommonPropTypes {
	platform: PlatformModel;
	runtime: RuntimeModel;
}

interface IVideoOverlayControlsStateTypes {
	attractorAnimatorsQueue: [string, boolean, [number, number]][],
	visibleTrayItemMatchIds: string[];
	isHover: boolean;
}

const cn = CN.create('controls', style);

@observer
export class VideoOverlayControls extends React.Component<IVideoOverlayControlsPropTypes, IVideoOverlayControlsStateTypes> {
	private get _isExtended(): boolean {
		const {platform, runtime} = this.props;

		return (
			this.state.isHover ||
			platform.twitch.context.isControlsVisible ||
			platform.twitch.context.isHighlighted ||
			runtime.runtimes.some(item => item.messages.length > 0)
		);
	}

	private _runtimeMatchIdsReactionDisposer: IReactionDisposer;

	private _runtimeMessagesReactionDisposer: IReactionDisposer;

	private _isExtendedReactionDisposer: IReactionDisposer;

	protected $refs: {
		[key: string]: React.RefObject<HTMLDivElement> | {[key: string]: React.RefObject<HTMLDivElement>}
	} = {
		node: React.createRef(),
		logoCircle: React.createRef(),
		logoText: React.createRef(),
		indicatorIncoming: React.createRef<HTMLDivElement>(),
		indicatorOutgoing: React.createRef<HTMLDivElement>(),
		trayItems: {},
	};

	protected $animations: {[key: string]: (Animation.Instance | Animation.TimelineInstance)} = {};

	public state: IVideoOverlayControlsStateTypes = {
		attractorAnimatorsQueue: [],
		visibleTrayItemMatchIds: [],
		isHover: false,
	};

	public componentDidMount(): void {
		this.props.runtime
			.addActivityIndicationCallback('incoming', this._onActivityIncoming)
			.addActivityIndicationCallback('outgoing', this._onActivityOutgoing);

		this._isExtendedReactionDisposer = reaction(
			() => this._isExtended,
			(isExtended) => {
				this._animateLogo(isExtended);
				this._animateTrayItems(isExtended);
			},
			{
				fireImmediately: true,
			}
		);

		this._runtimeMatchIdsReactionDisposer = reaction(
			() => this.props.runtime.runtimes.map(runtime => runtime.matchId),
			(currMatchIds, prevMatchIds) => {
				const nodeEl = this.$refs.node?.current as HTMLDivElement;

				this.setState({
					attractorAnimatorsQueue: [
						...this.state.attractorAnimatorsQueue,
						...currMatchIds.filter(matchId => !prevMatchIds.includes(matchId)).map(matchId => {
							return [
								`${matchId}-${uuid()}`,
								true,
								[nodeEl?.offsetWidth - 200, 0] as [number, number],
							];
						}),
						...prevMatchIds.filter(matchId => !currMatchIds.includes(matchId)).map(matchId => {
							return [
								`${matchId}-${uuid()}`,
								false,
								[nodeEl?.offsetWidth - 200, 0] as [number, number]
							];
						}),
					] as [string, boolean, [number, number]][],
				});
			}
		);

		this._runtimeMessagesReactionDisposer = reaction(
			() => this.props.runtime.runtimes.reduce((result, runtime) => {
				return [
					...result,
					...(!!runtime.messages.length ? [runtime.matchId] : []),
				];
			}, []),
			(currMatchIds, prevMatchIds) => {
				const matchIdsToRemove = prevMatchIds.filter(matchId => !currMatchIds.includes(matchId));

				this.setState({
					visibleTrayItemMatchIds: [
						...this.state.visibleTrayItemMatchIds.filter(matchId => !matchIdsToRemove.includes(matchId)),
						...currMatchIds.filter(matchId => !prevMatchIds.includes(matchId))
					],
				});
			}
		);
	}

	public componentDidUpdate(
		prevProps: Readonly<IVideoOverlayControlsPropTypes>,
		prevState: Readonly<IVideoOverlayControlsStateTypes>
	): void {
		this._animateTrayItemHover(prevState.visibleTrayItemMatchIds, this.state.visibleTrayItemMatchIds);
	}

	public componentWillUnmount(): void {
		this.props.runtime
			.removeActivityIndicationCallback('incoming', this._onActivityIncoming)
			.removeActivityIndicationCallback('outgoing', this._onActivityOutgoing);

		Object.values(this.$animations).forEach((item) => {
			Animation.pause(item);
		});

		this.$animations = undefined;

		if (_isFunction(this._isExtendedReactionDisposer)) {
			this._isExtendedReactionDisposer();
		}

		if (_isFunction(this._runtimeMatchIdsReactionDisposer)) {
			this._runtimeMatchIdsReactionDisposer();
		}

		if (_isFunction(this._runtimeMessagesReactionDisposer)) {
			this._runtimeMessagesReactionDisposer();
		}

		this.$refs = undefined;
	}

	private _onMouseOver = (event) => {
		event.preventDefault();

		this.setState({
			isHover: true,
		});
	}

	private _onMouseOut = (event) => {
		event.preventDefault();

		this.setState({
			isHover: false,
		});
	}

	private _animateLogo = (isExtended) => {
		if (this.$animations.logo) {
			Animation.pause(this.$animations.logo);
		}

		this.$animations.logo = (
			Animation.timeline({
				duration: isExtended ? 400 : 250,
				delay: 250,
				easing: Animation.Easing.Predefined.EXPO_OUT,
				autoplay: false,
			})
				.add({
					targets: this.$refs.logoText.current,
					color: isExtended ? '#ffffff' : '#323232',
					clipPath: (
						isExtended
							? 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)'
							: 'polygon(0% 0%, 15% 0%, 15% 100%, 0% 100%)'
					),
					translateX: isExtended ? 0 : -37,
				}, 0)
				.add({
					targets: this.$refs.logoCircle.current,
					scale: Number(!isExtended),
				}, 0)
		);

		Animation.play(this.$animations.logo);
	}

	private _animateTrayItems = (isExtended) => {
		if (this.$animations.trayItems) {
			Animation.pause(this.$animations.trayItems);
		}

		this.$animations.trayItems = Animation.simple({
			targets: Object.values(this.$refs.trayItems).map(ref => ref.current).filter(Boolean),
			right: (el, i) => {
				return isExtended ? 0 : -(55 * (i + 1) + 45);
			},
			opacity: Number(isExtended),
			scale: isExtended ? 1 : 0.1,
			duration: isExtended ? 600 : 200,
			delay: (el, i) => {
				if (!isExtended) {
					return 75 * i;
				}

				return 350 + 75 * i;
			},
			easing: isExtended ? Animation.Easing.Predefined.BACK_OUT : Animation.Easing.Predefined.EXPO_OUT,
		});
	}

	private _animateTrayItemHover = (prevMatchIds, currMatchIds) => {
		if (_isEqual(prevMatchIds, currMatchIds)) {
			return;
		}

		const currTargetEls = currMatchIds.map(currMatchId => this.$refs.trayItems[currMatchId]?.current);

		if (this.$animations.trayItemHover) {
			Animation.pause(this.$animations.trayItemHover);
		}

		this.$animations.trayItemHover = Animation.timeline({
			autoplay: false,
		});

		if (currTargetEls.length) {
			this.$animations.trayItemHover
				.add({
					targets: currTargetEls,
					width: (el, i) => {
						return el?.querySelector(`#tray-item-label-${currMatchIds[i]}`)?.offsetWidth || 45;
					},
					duration: 400,
					easing: Animation.Easing.Predefined.EXPO_OUT,
				}, 0);
		}

		this.$animations.trayItemHover
			.add({
				targets: (
					Object.entries(this.$refs.trayItems)
						.filter(([matchId, ref]) => !!ref.current && !currMatchIds.includes(matchId))
						.map(([,ref]) => ref.current)
				),
				width: 45,
				duration: 200,
				easing: Animation.Easing.Predefined.EXPO_IN_OUT,
			}, 200);

		Animation.play(this.$animations.trayItemHover);
	}

	private _onActivityIncoming = () => {
		if (this.$animations.incoming) {
			Animation.pause(this.$animations.incoming);
		}

		this.$animations.incoming = (
			Animation.timeline({
				targets: this.$refs.indicatorIncoming.current,
				autoplay: false,
			})
				.add({
					clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
					easing: Animation.Easing.Predefined.BACK_OUT,
					duration: 250
				})
				.add({
					clipPath: 'polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)',
					easing: Animation.Easing.Predefined.EXPO_OUT,
					duration: 180,
				})
		);

		Animation.play(this.$animations.incoming);
	};

	private _onActivityOutgoing = () => {
		if (this.$animations.outgoing) {
			Animation.pause(this.$animations.outgoing);
		}

		this.$animations.outgoing = (
			Animation.timeline({
				targets: this.$refs.indicatorOutgoing.current,
				autoplay: false,
			})
				.add({
					clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
					easing: Animation.Easing.Predefined.BACK_OUT,
					duration: 250
				})
				.add({
					clipPath: 'polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)',
					easing: Animation.Easing.Predefined.EXPO_OUT,
					duration: 180,
				})
		);

		Animation.play(this.$animations.outgoing);
	}

	private _renderAttractor(): React.ReactNode {
		return (
			<div
				className={cn.get('attractor')}>
				{this.state.attractorAnimatorsQueue.map(([id, isVisible, coordinates], index) => {
					return (
						<div
							key={`attractor-animator-${id}-${isVisible ? 'in' : 'out'}`}
							ref={(el) => {
								if (!el || !!this.$animations[id]) {
									return;
								}

								const size = Math.max(el.offsetWidth, el.offsetHeight);

								el.style.width = `${size}px`;
								el.style.height = `${size}px`;

								el.style.top = `${coordinates[1]}px`;
								el.style.left = `${coordinates[0]}px`;

								el.style.marginTop = `-${size / 2}px`;
								el.style.marginLeft = `-${size / 2}px`;

								el.style.borderRadius = `${size * 2}px`;

								el.style.transform = 'scale(0.01)';
								el.style.opacity = '0';

								this.$animations[id] = (
									Animation.timeline({
										targets: el,
										autoplay: false,
										delay: index * 25,
										complete: () => {
											this.setState({
												attractorAnimatorsQueue: (
													this.state.attractorAnimatorsQueue.filter(
														([candidateId]) => candidateId !== id
													)
												),
											});

											delete this.$animations[id];
										}
									})
										.add({
											backgroundColor: isVisible ? '#ffe04d' : '#ff984d',
											scale: 2,
											opacity: 0.25,
											duration: 300,
											easing: Animation.Easing.Predefined.EXPO_IN_OUT,
										})
										.add({
											backgroundColor: '#835AFF',
											scale: 0.01,
											opacity: 0.1,
											duration: 200,
											easing: Animation.Easing.Predefined.BOUNCE_OUT,
										})
								);

								Animation.play(this.$animations[id]);
							}}
							className={cn.get('attractor', 'animator', [isVisible ? 'in' : 'out'])} />
					);
				})}
			</div>
		);
	}

	private _renderPanel(): React.ReactNode {
		return (
			<div
				className={cn.get('panel')}>
				{this._renderPanelLogo()}
				{this._renderPanelIndicator()}
			</div>
		);
	}

	private _renderPanelLogo(): React.ReactNode {
		return (
			<div
				className={cn.get('panel', 'logo')}>
				<div
					ref={this.$refs.logoCircle as React.RefObject<HTMLDivElement>}
					className={cn.get('panel', 'logo', 'circle')} />
				<div
					ref={this.$refs.logoText as React.RefObject<HTMLDivElement>}
					className={cn.get('panel', 'logo', 'text')}>
					Brandis
				</div>
			</div>
		);
	}

	private _renderPanelIndicator(): React.ReactNode {
		return (
			<div
				className={cn.get('panel', 'indicator')}>
				<div
					ref={this.$refs.indicatorIncoming as React.RefObject<HTMLDivElement>}
					className={cn.get('panel', 'indicator', 'item', ['incoming'])} />
				<div
					ref={this.$refs.indicatorOutgoing as React.RefObject<HTMLDivElement>}
					className={cn.get('panel', 'indicator', 'item', ['outgoing'])} />
			</div>
		);
	}

	private _renderTray(): React.ReactNode {
		return (
			<div
				className={cn.get('tray')}>
				{this.props.runtime.runtimes.map(this._renderTrayItem)}
			</div>
		);
	}

	private _renderTrayItem = (item) => {
		const {runtime} = this.props;

		const intl = runtime.application.getModel('intl');

		return (
			<div
				key={item?.matchId}
				ref={((matchId) => {
					if (!Object.prototype.hasOwnProperty.call(this.$refs.trayItems, matchId)) {
						this.$refs.trayItems[matchId] = React.createRef();
					}

					return this.$refs.trayItems[matchId];
				})(item?.matchId)}
				className={cn.get('tray', 'item', [!!item?.state?.isVisible && 'active'])}
				onMouseEnter={(event) => {
					event.preventDefault();

					this.setState({
						visibleTrayItemMatchIds: (
							[...this.state.visibleTrayItemMatchIds, item.matchId]
						),
					});
				}}
				onMouseLeave={(event) => {
					event.preventDefault();

					if (item.messages?.length > 0) {
						return;
					}

					this.setState({
						visibleTrayItemMatchIds: (
							this.state.visibleTrayItemMatchIds.filter(hoveredMatchId => hoveredMatchId !== item.matchId)
						),
					});
				}}
				onClick={(event) => {
					event.preventDefault();

					const coordinates = Utility.Pointer.getCoordinatesRelativeToElement(
						event,
						this.$refs.node.current
					);

					const isVisible = !item?.state?.isVisible;

					this.setState({
						attractorAnimatorsQueue: [
							...this.state.attractorAnimatorsQueue,
							[[item?.matchId, uuid()].join('-'), isVisible, [coordinates.x, coordinates.y]],
						],
					}, () => {
						runtime
							.updateRuntimeState(item?.matchId, {
								isVisible: isVisible,
							});

						item.engine
							.analytics
							.track({
								category: 'interaction',
								event: isVisible ? 'maximize' : 'minimize',
								source: 'tray',
							});
					});
				}}>
				<div
					className={cn.get('tray', 'item', 'content')}>
					<div
						id={`tray-item-label-${item.matchId}`}
						className={cn.get('tray', 'item', 'content', 'label')}>
						<div
							className={cn.get('tray', 'item', 'content', 'label', 'name')}>
							{item.meta.name}
						</div>
						<div
							className={cn.get('tray', 'item', 'content', 'label', 'status')}>
							{(
								intl.getMessage(
									!!item?.state?.isVisible
										? 'Runtime.Interactive.Status.Hide'
										: 'Runtime.Interactive.Status.Show'
								)
							)}
						</div>
					</div>
				</div>
				<div
					className={cn.get('tray', 'item', 'messages')}>
					{item.messages.map(message => (
						<div
							key={message.id}
							className={cn.get('tray', 'item', 'messages', 'item')}>
							<div
								className={cn.get('tray', 'item', 'messages', 'item', 'title')}>
								{message.title}
							</div>
							<div
								className={cn.get('tray', 'item', 'messages', 'item', 'text')}>
								{message.text}
							</div>
							{Array.isArray(message.actions) && (
								<div
									className={cn.get('tray', 'item', 'messages', 'item', 'actions')}>
									{message.actions.map(action => (
										<button
											key={action.id}
											className={cn.get('tray', 'item', 'messages', 'item', 'actions', 'item')}
											onClick={(event) => {
												event.preventDefault();
												event.stopPropagation();

												if (!_isFunction(action.onPress)) {
													return;
												}

												action.onPress();
											}}>
											{action.label}
										</button>
									))}
								</div>
							)}
						</div>
					))}
				</div>
			</div>
		);
	}

	public render(): React.ReactNode {
		return (
			<div
				ref={this.$refs.node as React.RefObject<HTMLDivElement>}
				id={this.props.id}
				className={CN.join(cn.get([this._isExtended && 'extended']), this.props.className)}
				style={this.props.style}
				onMouseOver={this._onMouseOver}
				onMouseOut={this._onMouseOut}>
				{this._renderAttractor()}
				{this._renderPanel()}
				{this._renderTray()}
			</div>
		);
	}
}
